/* ==========================================================================
   Ozeret -- turning phone notifications on, and choosing which

   PushSettings is the whole thing, in Settings. PushNudge is the short
   version, at the top of the lists where news lands, for somebody who has
   not turned them on yet -- and it goes away for good once dismissed.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { api } from '../api';
import {
  askedHere, mayAsk, markAsked, onIos, pushState, turnOff, turnOn, type PushState,
} from '../lib/push';
import { nav, setTexts, state, toast } from '../store';
import { Card, SectionHead, Switch } from './ui';

const es = () => state.lang === 'es';
const L = (en: string, sp: string) => (es() ? sp : en);

const LABELS: Record<string, { en: string; es: string; hint?: [string, string] }> = {
  invited: { en: 'A home invites me to a job', es: 'Una casa me invita a un trabajo' },
  booked: { en: 'I am booked', es: 'Me contratan' },
  changed: { en: 'A visit is cancelled or changed', es: 'Una visita se cancela o cambia' },
  nearby: {
    en: 'A new job near me that fits my calendar', es: 'Un trabajo nuevo cerca que cabe en mi calendario',
    hint: ['At most one a day.', 'Como máximo uno al día.'],
  },
  applied: { en: 'Somebody is available, or asks for more', es: 'Alguien está disponible o pide más' },
  answered: { en: 'An invitation is answered', es: 'Responden a una invitación' },
  tomorrow: { en: 'The evening before a visit', es: 'La noche antes de una visita' },
  review: { en: 'A reminder to write a review', es: 'Un recordatorio para escribir una reseña' },
  message: {
    en: 'A new chat message', es: 'Un mensaje nuevo en el chat',
    hint: ['Off unless you turn it on. One per conversation every 10 minutes at most.',
      'Apagado a menos que lo actives. Como máximo uno por conversación cada 10 minutos.'],
  },
};

const BLOCKED = () => (onIos()
  ? L('Notifications are switched off for Ozeret on this iPhone. Open the iPhone\'s Settings, then Notifications, then Ozeret, and turn on Allow Notifications.',
    'Las notificaciones de Ozeret están desactivadas en este iPhone. Abre Ajustes del iPhone, luego Notificaciones, luego Ozeret, y activa Permitir notificaciones.')
  : L('Notifications are blocked for Ozeret in this browser. Allow them in the browser\'s site settings (the icon beside the address), then come back.',
    'Las notificaciones están bloqueadas para Ozeret en este navegador. Permítelas en la configuración del sitio (el icono junto a la dirección) y vuelve.'));

const QUIET = () => L(
  'Quiet at night (10pm–7am) and on Shabbat and Jewish holidays. Anything that comes in then arrives afterwards.',
  'Sin avisos de noche (10pm–7am) ni en Shabat y fiestas judías. Lo que llegue entonces se envía después.',
);

function usePushState(): [PushState | null, (s: PushState) => void] {
  const [st, setSt] = useState<PushState | null>(null);
  useEffect(() => {
    let live = true;
    void pushState().then((s) => { if (live) setSt(s); });
    return () => { live = false; };
  }, []);
  return [st, setSt];
}

async function switchOn(set: (s: PushState) => void): Promise<void> {
  try {
    const s = await turnOn(state.lang);
    set(s);
    if (s === 'on') toast(L('Notifications are on for this device.', 'Notificaciones activadas en este dispositivo.'));
    else if (s === 'blocked') toast(L('Blocked in this browser. Allow them in its settings.', 'Bloqueadas en este navegador. Permítelas en su configuración.'));
  } catch (e) {
    toast((e as Error).message);
  }
}

/** What to do on this device, said for where it stands. */
function Standing({ st, set }: { st: PushState; set: (s: PushState) => void }) {
  if (st === 'needs-install') {
    return (
      <span className="hint">
        {L(
          'On an iPhone, notifications only work once Ozeret is on your home screen. In Safari tap Share, then "Add to Home Screen", open Ozeret from there, and turn them on here.',
          'En iPhone, las notificaciones solo funcionan con Ozeret en la pantalla de inicio. En Safari toca Compartir, luego "Agregar a inicio", abre Ozeret desde ahí y actívalas aquí.',
        )}
      </span>
    );
  }
  if (st === 'blocked') {
    return (
      <span className="hint">
        {BLOCKED()}
      </span>
    );
  }
  if (st === 'unsupported') {
    return (
      <span className="hint">
        {L('This browser cannot show notifications. Chrome or Safari on a phone can.',
          'Este navegador no puede mostrar notificaciones. Chrome o Safari en el teléfono sí.')}
      </span>
    );
  }
  if (st === 'off') {
    return (
      <button type="button" className="btn block" onClick={() => void switchOn(set)}>
        {L('Turn on notifications', 'Activar notificaciones')}
      </button>
    );
  }
  return null;
}

export function PushSettings() {
  const [st, setSt] = usePushState();
  const [prefs, setPrefs] = useState<{ kinds: string[]; off: string[] } | null>(null);

  useEffect(() => {
    if (state.me && !state.me.isDemo) {
      api.pushPrefs().then(setPrefs).catch(() => { /* offline: the switches wait */ });
    }
  }, []);

  if (!state.me || state.me.isDemo || st === null) return null;

  const flip = async (kind: string) => {
    if (!prefs) return;
    const off = prefs.off.includes(kind) ? prefs.off.filter((k) => k !== kind) : [...prefs.off, kind];
    setPrefs({ ...prefs, off });
    try {
      const r = await api.setPushPrefs(off);
      setPrefs({ ...prefs, off: r.off });
    } catch (e) {
      setPrefs(prefs);
      toast((e as Error).message);
    }
  };

  return (
    <Card>
      <SectionHead title={L('Notifications', 'Notificaciones')} />
      <span className="sub">
        {st === 'on'
          ? L('On for this device.', 'Activadas en este dispositivo.')
          : L('Get a notification on this phone when something happens, without opening Ozeret.',
            'Recibe un aviso en este teléfono cuando pase algo, sin abrir Ozeret.')}
      </span>
      <Standing st={st} set={setSt} />

      {st === 'on' && prefs && (
        <div className="col" style={{ gap: 10 }}>
          {prefs.kinds.map((k) => (
            <div key={k} className="row" style={{ gap: 12, alignItems: 'center' }}>
              <div className="col grow" style={{ gap: 2 }}>
                <span>{LABELS[k] ? (es() ? LABELS[k].es : LABELS[k].en) : k}</span>
                {LABELS[k]?.hint && <span className="hint">{es() ? LABELS[k].hint![1] : LABELS[k].hint![0]}</span>}
              </div>
              <Switch on={!prefs.off.includes(k)} onClick={() => void flip(k)} />
            </div>
          ))}
          <span className="hint">
            {L('These apply on every device where notifications are on.',
              'Esto aplica en todos tus dispositivos con notificaciones.')}
          </span>
        </div>
      )}

      <span className="hint">{QUIET()}</span>

      {st === 'on' && (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn ghost sm"
            onClick={async () => {
              try {
                const r = await api.pushTest();
                toast(r.result?.startsWith('sent')
                  ? L('Sent. It should appear in a moment.', 'Enviado. Aparecerá en un momento.')
                  : L('It did not go through. Try turning notifications off and on again.',
                    'No se pudo enviar. Desactiva y vuelve a activar las notificaciones.'));
              } catch (e) {
                toast((e as Error).message);
              }
            }}
          >
            {L('Send me a test', 'Enviarme una prueba')}
          </button>
          <button
            type="button"
            className="btn quiet sm"
            onClick={async () => setSt(await turnOff())}
          >
            {L('Turn off on this device', 'Desactivar en este dispositivo')}
          </button>
        </div>
      )}
    </Card>
  );
}

const NUDGE_GONE = 'ozeret.push.nudge.dismissed';

/** A short "turn them on" at the top of a list, until they do or say no. */
export function PushNudge() {
  const [st, setSt] = usePushState();
  const [gone, setGone] = useState(() => {
    try { return localStorage.getItem(NUDGE_GONE) === '1'; } catch { return false; }
  });
  /* The one-time question at the top asks the same thing; one at a time. */
  const askPending = !askedHere() && mayAsk();
  if (!state.me || state.me.isDemo || gone || st !== 'off' || askPending) return null;
  const dismiss = () => {
    setGone(true);
    try { localStorage.setItem(NUDGE_GONE, '1'); } catch { /* shows again next time */ }
  };
  const cleaner = state.role === 'cleaner';
  return (
    <Card className="tint">
      <b>{L('Know the moment something happens', 'Entérate al momento')}</b>
      <span className="hint">
        {cleaner
          ? L('A notification when a home invites you, books you, or changes a visit.',
            'Un aviso cuando una casa te invita, te contrata o cambia una visita.')
          : L('A notification when a cleaner is available, answers your invitation, or changes a visit.',
            'Un aviso cuando alguien está disponible, responde a tu invitación o cambia una visita.')}
      </span>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn sm" onClick={() => void switchOn(setSt)}>
          {L('Turn on', 'Activar')}
        </button>
        <button type="button" className="btn quiet sm" onClick={() => nav('settings')}>
          {L('Choose which', 'Elegir cuáles')}
        </button>
        <button type="button" className="btn quiet sm" onClick={dismiss}>
          {L('Not now', 'Ahora no')}
        </button>
      </div>
    </Card>
  );
}


/** Asked after every sign-in -- the nearest thing to "on by default" a
    browser allows. Waits for the welcome tour to finish, and sits at the top
    so it never covers the install offer at the bottom. If the phone itself
    has notifications blocked, the box cannot fix that, so it says where to. */
export function PushAsk() {
  /* Two questions after signing in, one at a time: notifications first,
     then texts. Never both boxes on screen at once. */
  const [notifySettled, setNotifySettled] = useState(false);
  return notifySettled ? <TextsAsk /> : <NotifyAsk onSettled={() => setNotifySettled(true)} />;
}

function NotifyAsk({ onSettled }: { onSettled: () => void }) {
  const [show, setShow] = useState<PushState | null>(null);
  useEffect(() => {
    if (askedHere()) { onSettled(); return; }
    let live = true;
    void pushState().then((st) => {
      if (!live) return;
      if (st === 'off' || st === 'blocked') setShow(st);
      else onSettled();
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!show || !state.me || state.me.isDemo || state.tour) return null;
  const done = () => {
    setShow(null);
    markAsked();
    onSettled();
  };
  const cleaner = state.role === 'cleaner';
  return (
    <div className="installbar pushask" role="dialog" aria-label={L('Notifications', 'Notificaciones')}>
      <div className="col grow" style={{ gap: 3 }}>
        <b style={{ fontSize: 14 }}>
          {show === 'blocked'
            ? L('Notifications are off', 'Las notificaciones están apagadas')
            : L('Turn on notifications?', '¿Activar notificaciones?')}
        </b>
        <span className="hint">
          {show === 'blocked'
            ? BLOCKED()
            : cleaner
              ? L('So you know the moment a home invites you or books you.',
                'Para saber al momento cuando una casa te invita o te contrata.')
              : L('So you know the moment a cleaner answers or is available.',
                'Para saber al momento cuando alguien responde o está disponible.')}
        </span>
      </div>
      <div className="col" style={{ gap: 6 }}>
        {show === 'off' && (
          <button
            type="button"
            className="btn sm"
            onClick={async () => {
              done();
              try {
                const s = await turnOn(state.lang);
                if (s === 'on') toast(L('Notifications are on. Choose which in Settings.', 'Notificaciones activadas. Elige cuáles en Ajustes.'));
                else if (s === 'blocked') toast(BLOCKED());
              } catch (e) {
                toast((e as Error).message);
              }
            }}
          >
            {L('Turn on', 'Activar')}
          </button>
        )}
        <button type="button" className="btn quiet sm" onClick={done}>
          {show === 'blocked' ? L('OK', 'OK') : L('Not now', 'Ahora no')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ texts */

export const TEXTS_ASKED = 'ozeret.texts.asked';

const TEXTS_FINE = () => L(
  'Optional. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
  'Opcional. La frecuencia varía. Pueden aplicar tarifas de mensajes y datos. Responda STOP para cancelar, HELP para ayuda.',
);

function SmallPrint() {
  return (
    <>
      {TEXTS_FINE()}{' '}
      <a href="/terms" target="_blank" rel="noreferrer">{L('Terms', 'Términos')}</a>
      {' · '}
      <a href="/privacy" target="_blank" rel="noreferrer">{L('Privacy', 'Privacidad')}</a>
    </>
  );
}

/** "Get a text when someone answers or books you?" after signing in, for
    anybody who has not said yes. Tapping Yes is the agreement; nothing is
    ever switched on for them. Asked again on the next sign-in if they say
    not now. */
function TextsAsk() {
  const [gone, setGone] = useState(() => {
    try { return localStorage.getItem(TEXTS_ASKED) === '1'; } catch { return false; }
  });
  if (gone || !state.me || state.me.isDemo || state.me.textsOn || state.tour) return null;
  const done = () => {
    setGone(true);
    try { localStorage.setItem(TEXTS_ASKED, '1'); } catch { /* asks again next time */ }
  };
  const cleaner = state.role === 'cleaner';
  return (
    <div className="installbar pushask" role="dialog" aria-label={L('Texts', 'Mensajes')}>
      <div className="col grow" style={{ gap: 3 }}>
        <b style={{ fontSize: 14 }}>
          {cleaner
            ? L('Get a text when a home invites or books you?', '¿Recibir un mensaje cuando una casa te invite o te contrate?')
            : L('Get a text when someone answers or books?', '¿Recibir un mensaje cuando alguien responda o acepte?')}
        </b>
        <span className="hint"><SmallPrint /></span>
      </div>
      <div className="col" style={{ gap: 6 }}>
        <button type="button" className="btn sm" onClick={() => { done(); void setTexts(true, 'ask'); }}>
          {L('Yes, text me', 'Sí, envíenme')}
        </button>
        <button type="button" className="btn quiet sm" onClick={done}>
          {L('Not now', 'Ahora no')}
        </button>
      </div>
    </div>
  );
}

/** The switch in Settings. */
export function TextsSettings() {
  const me = state.me;
  if (!me || me.isDemo) return null;
  return (
    <Card>
      <SectionHead title={L('Texts', 'Mensajes de texto')} />
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <div className="col grow" style={{ gap: 2 }}>
          <span>
            {state.role === 'cleaner'
              ? L('Text me when a home invites or books me', 'Envíenme un mensaje cuando una casa me invite o me contrate')
              : L('Text me when someone answers my listing or books me', 'Envíenme un mensaje cuando alguien responda a mi anuncio o me contrate')}
          </span>
          {me.phone && <span className="hint">{L('To', 'A')} {me.phone}</span>}
        </div>
        <Switch on={!!me.textsOn} onClick={() => void setTexts(!me.textsOn, 'settings')} />
      </div>
      <span className="hint">
        {L('Only when a phone notification could not reach you, and only for invitations, bookings, cancellations and answers. ',
          'Solo cuando una notificación no pudo llegarte, y solo para invitaciones, contrataciones, cancelaciones y respuestas. ')}
        <SmallPrint />
      </span>
      {me.textsOn && !me.textsReady && (
        <span className="hint">
          {L('Ozeret is finishing setting up texts. Your first one will confirm they are on.',
            'Ozeret está terminando de configurar los mensajes. El primero confirmará que están activados.')}
        </span>
      )}
      <span className="hint">
        {L('If you replied STOP to an Ozeret text, reply START to turn them back on.',
          'Si respondió STOP a un mensaje de Ozeret, responda START para activarlos de nuevo.')}
      </span>
    </Card>
  );
}
